import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';

import logo from '../assets/logo.png';
import PolishFlag from './PolishFlag';
import EnglishFlag from './EnglishFlag';
import ThemeSwitch from './ThemeSwitch';
import { dogs as staticDogs } from '../data/dogs';
import { fetchDogs } from '../lib/api';
import { useSupabaseData } from '../lib/useSupabaseData';
import { useTheme } from '../lib/theme';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { classicEnabled } = useTheme();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDogsOpen, setMobileDogsOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const { pathname } = useLocation();

  // Psy pobierane z bazy — dzięki temu nowo dodane w CMS od razu są w menu
  const { data: dogs } = useSupabaseData(fetchDogs, staticDogs, 'dogs');

  // Zamknij menu przy zmianie podstrony
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [pathname]);

  // Zablokuj przewijanie tła przy otwartym menu mobilnym
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  // Zamknij mobile menu po kliknięciu poza nim
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Zamknij mobile menu przy przejściu na desktop
  useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 1024) setMobileMenuOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Zamknij na Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMobileMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.ourDogs'), path: '/nasze-psy', isDropdown: true },
    { name: t('nav.litters'), path: '/mioty' },
    { name: t('nav.shows'), path: '/wystawy' },
    { name: t('nav.aboutBreed'), path: '/o-rasie' },
    { name: t('nav.gallery'), path: '/galeria' },
    { name: t('nav.contact'), path: '/kontakt' },
  ];

  const females = dogs.filter((d) => d.category === 'females');
  const males = dogs.filter((d) => d.category === 'males');
  const inMemory = dogs.filter((d) => d.category === 'memory');

  const groups = [
    { title: t('ourDogs.females_section_title'), items: females },
    { title: t('ourDogs.males_section_title'), items: males },
    { title: t('ourDogs.youth_section_title'), items: inMemory },
  ].filter((g) => g.items.length > 0);

  const LangButtons = ({ size = 22 }) => (
    <div className="flex items-center gap-x-1.5 rounded-full border border-brand-accent/45 bg-brand-bg-side/60 p-1">
      {[
        { lng: 'pl', Flag: PolishFlag, label: 'Polski' },
        { lng: 'en', Flag: EnglishFlag, label: 'English' },
      ].map(({ lng, Flag, label }) => (
        <button
          key={lng}
          onClick={() => i18n.changeLanguage(lng)}
          aria-label={label}
          aria-pressed={i18n.language === lng}
          className={`rounded-full transition-all duration-300 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent ${
            i18n.language === lng ? 'opacity-100 ring-1 ring-brand-accent/70' : 'opacity-55 hover:opacity-90'
          }`}
        >
          <Flag size={size} />
        </button>
      ))}
    </div>
  );

  const navLinkClass = ({ isActive }) =>
    `relative uppercase tracking-[0.18em] transition-colors duration-300 hover:text-brand-accent after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-brand-accent after:transition-all after:duration-300 ${
      isActive ? 'text-brand-accent after:w-full' : 'text-brand-text-light after:w-0 hover:after:w-full'
    }`;

  return (
    <header className="py-4 sm:py-6">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Pasek narzędzi: przełącznik designu + języki | hamburger */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {/* Przełącznik designu widoczny tylko, gdy admin odblokował stary design w CMS */}
            {classicEnabled && <ThemeSwitch />}
            <LangButtons />
          </div>

          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-brand-accent/45 bg-brand-bg-side/60 text-brand-text-light hover:text-brand-accent hover:border-brand-accent/80 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
            aria-label={isMobileMenuOpen ? 'Zamknij menu' : 'Otwórz menu'}
            aria-expanded={isMobileMenuOpen}
          >
            <motion.span animate={{ rotate: isMobileMenuOpen ? 90 : 0 }} transition={{ duration: 0.25 }}>
              {isMobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
            </motion.span>
          </button>
        </div>

        {/* Logo */}
        <div className="flex justify-center">
          <Link to="/" className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent rounded-lg">
            <img
              src={logo}
              alt="Etsuko logo"
              className="h-20 sm:h-24 lg:h-32 w-auto transition-transform duration-500 hover:scale-[1.04]"
            />
          </Link>
        </div>

        <div className="w-full max-w-4xl mx-auto h-px bg-gradient-to-r from-transparent via-brand-accent/40 to-transparent my-4" />

        {/* Nawigacja desktop */}
        <nav className="hidden lg:flex flex-wrap justify-center items-center gap-x-7 gap-y-3 text-[0.8rem]">
          {navLinks.map((link) =>
            link.isDropdown ? (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <NavLink to={link.path} className={navLinkClass}>
                  <span className="inline-flex items-center gap-1.5">
                    {link.name}
                    <FaChevronDown className={`h-2 w-2 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </span>
                </NavLink>
                <AnimatePresence>
                  {isDropdownOpen && groups.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-4 z-30"
                    >
                      <div className="w-[22rem] rounded-xl border border-brand-accent/25 bg-brand-bg-side/95 p-5 text-left shadow-2xl backdrop-blur-md">
                        <div className="grid grid-cols-2 gap-x-5 gap-y-4">
                          {groups.map((g) => (
                            <div key={g.title}>
                              <h4 className="mb-2 border-b border-brand-accent/40 pb-1 text-[0.7rem] font-semibold uppercase tracking-widest text-brand-accent">
                                {g.title}
                              </h4>
                              {g.items.map((dog) => (
                                <Link
                                  key={dog.id}
                                  to={`/nasze-psy/${dog.id}`}
                                  className="block rounded py-1 text-sm text-brand-text-light/90 transition-colors duration-200 hover:text-brand-accent"
                                >
                                  {dog.name}
                                </Link>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink key={link.name} to={link.path} className={navLinkClass}>
                {link.name}
              </NavLink>
            )
          )}
        </nav>

        {/* Nawigacja mobilna */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.nav
              ref={mobileMenuRef}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              className="lg:hidden w-full overflow-hidden"
            >
              <div className="mt-2 max-h-[70vh] overflow-y-auto overscroll-contain rounded-xl border border-brand-accent/25 bg-brand-bg-side/95 p-3 shadow-2xl backdrop-blur-md">
                {navLinks.map((link) =>
                  link.isDropdown ? (
                    <div key={link.name}>
                      <div className="flex items-center">
                        <NavLink
                          to={link.path}
                          className={({ isActive }) =>
                            `flex-1 rounded-lg px-4 py-3 uppercase tracking-[0.18em] text-sm transition-colors duration-300 ${
                              isActive ? 'bg-brand-accent/10 font-semibold text-brand-accent' : 'text-brand-text-light hover:bg-brand-accent/5 hover:text-brand-accent'
                            }`
                          }
                        >
                          {link.name}
                        </NavLink>
                        <button
                          onClick={() => setMobileDogsOpen((v) => !v)}
                          aria-label="Rozwiń listę psów"
                          aria-expanded={mobileDogsOpen}
                          className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-brand-text-light/70 hover:text-brand-accent"
                        >
                          <FaChevronDown className={`h-3 w-3 transition-transform duration-300 ${mobileDogsOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </div>

                      <AnimatePresence>
                        {mobileDogsOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                          >
                            <div className="ml-4 space-y-1 border-l border-brand-accent/30 pl-4 pb-2">
                              {groups.map((g) => (
                                <div key={g.title}>
                                  <div className="mb-1 mt-3 text-[0.65rem] font-semibold uppercase tracking-widest text-brand-accent">
                                    {g.title}
                                  </div>
                                  {g.items.map((dog) => (
                                    <Link
                                      key={dog.id}
                                      to={`/nasze-psy/${dog.id}`}
                                      className="block py-1.5 text-sm text-brand-text-light/85 transition-colors duration-200 hover:text-brand-accent"
                                    >
                                      {dog.name}
                                    </Link>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <NavLink
                      key={link.name}
                      to={link.path}
                      className={({ isActive }) =>
                        `block rounded-lg px-4 py-3 uppercase tracking-[0.18em] text-sm transition-colors duration-300 ${
                          isActive ? 'bg-brand-accent/10 font-semibold text-brand-accent' : 'text-brand-text-light hover:bg-brand-accent/5 hover:text-brand-accent'
                        }`
                      }
                    >
                      {link.name}
                    </NavLink>
                  )
                )}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
